/**
 * BaseEventListener
 * 
 * Base class for all event listeners in the alert system.
 * Provides common functionality for event listeners, including:
 * - Starting and stopping listeners
 * - Tracking event processing statistics
 * - Common event handling patterns
 * 
 * @module BaseEventListener
 */

/**
 * Logger for BaseEventListener
 */
const Logger = {
  log: (...args) => console.log('[BaseEventListener]', ...args),
  warn: (...args) => console.warn('[BaseEventListener]', ...args),
  error: (...args) => console.error('[BaseEventListener]', ...args),
  debug: (...args) => console.debug('[BaseEventListener]', ...args)
};

/**
 * Base class for all event listeners
 * @class BaseEventListener
 */
class BaseEventListener {
  /**
   * Creates an instance of BaseEventListener
   * @param {Object} alertService - Service for alert operations
   * @param {string} listenerType - Type of listener (for logging)
   */
  constructor(alertService, listenerType) {
    if (this.constructor === BaseEventListener) {
      throw new Error('BaseEventListener is an abstract class and cannot be instantiated directly');
    }
    
    this.alertService = alertService;
    this.listenerType = listenerType || 'base';
    this.active = false;
    this.eventsProcessed = 0;
    this.lastEventTime = null;
    this.startTime = null;
    
    // Bind methods to ensure 'this' context
    this.handleEvent = this.handleEvent.bind(this);
    
    Logger.log(`${this.listenerType} listener created`);
  }

  /**
   * Start the event listener
   * @returns {Promise<boolean>} True if the listener was started successfully
   */
  async start() {
    if (this.active) {
      Logger.warn(`${this.listenerType} listener is already active`);
      return true;
    }
    
    try {
      await this._setupEventListeners();
      this.active = true;
      this.startTime = Date.now();
      Logger.log(`${this.listenerType} listener started`);
      return true;
    } catch (error) {
      Logger.error(`Failed to start ${this.listenerType} listener`, error);
      return false;
    }
  }

  /**
   * Stop the event listener
   * @returns {Promise<boolean>} True if the listener was stopped successfully
   */
  async stop() {
    if (!this.active) {
      Logger.warn(`${this.listenerType} listener is not active`);
      return true;
    }
    
    try {
      await this._removeEventListeners();
      this.active = false;
      Logger.log(`${this.listenerType} listener stopped`);
      return true;
    } catch (error) {
      Logger.error(`Failed to stop ${this.listenerType} listener`, error);
      return false;
    }
  }

  /**
   * Setup event listeners - to be implemented by subclasses
   * @protected
   * @returns {Promise<void>}
   */
  async _setupEventListeners() {
    throw new Error('_setupEventListeners must be implemented by subclass');
  }

  /**
   * Remove event listeners - to be implemented by subclasses
   * @protected
   * @returns {Promise<void>}
   */
  async _removeEventListeners() {
    throw new Error('_removeEventListeners must be implemented by subclass');
  }

  /**
   * Handle an event - to be implemented by subclasses
   * @param {Object} event - The event to handle
   * @returns {Promise<boolean>} True if the event was handled successfully
   */
  async handleEvent(event) {
    if (!this.active) {
      Logger.warn(`${this.listenerType} listener is not active, ignoring event`);
      return false;
    }
    
    try {
      // Track event statistics
      this.eventsProcessed++;
      this.lastEventTime = Date.now();
      
      // Actual event handling is implemented by subclasses
      return true;
    } catch (error) {
      Logger.error(`Error handling event in ${this.listenerType} listener`, error);
      return false;
    }
  }

  /**
   * Get the number of events processed
   * @returns {number} Number of events processed
   */
  getEventsProcessed() {
    return this.eventsProcessed;
  }

  /**
   * Get the timestamp of the last event
   * @returns {number|null} Timestamp of the last event or null if no events processed
   */
  getLastEventTime() {
    return this.lastEventTime;
  }

  /**
   * Get the listener status
   * @returns {Object} Status information
   */
  getStatus() {
    return {
      type: this.listenerType,
      active: this.active,
      eventsProcessed: this.eventsProcessed,
      lastEventTime: this.lastEventTime,
      uptime: this.startTime ? Date.now() - this.startTime : 0
    };
  }

  /**
   * Reset event statistics
   */
  resetStats() {
    this.eventsProcessed = 0;
    this.lastEventTime = null;
  }
}

export default BaseEventListener;
